import { BookOpen, Mail, ShieldCheck, User } from 'lucide-react';
import type { JSX } from 'react';
import { Link } from 'react-router-dom';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/features/auth/session';
import { roleLabel } from '@/features/membership/labels';
import { useActiveOrganization } from '@/features/organization/active-organization-context';
import { useIsPersonalSpace, usePersonalSpace } from '@/features/personal-space/hooks';
import { PreferredMethodCard } from '@/features/students/preferred-method-card';
import { useMyStudentProfile } from '@/features/students/hooks';
import { useHasCapability } from '@/lib/capabilities';

/**
 * "Profil Saya" - who the reader is on this platform, and the one setting
 * about their own memorization they can actually change.
 *
 * Deliberately read-only about identity: the API exposes no endpoint for
 * changing a name, an email or a password from a signed-in session (only
 * the token-based reset flow), so this page shows those and does not offer
 * a form that could not be saved. `PreferredMethodCard` is the exception
 * and the reason this page is worth visiting - it is genuinely editable by
 * the student themselves (Phase 8, Pilar A), including the three-question
 * assessment that *suggests* a method, and it had been reachable only
 * through `/students/:id`, the management workspace a solo learner has no
 * reason to open.
 *
 * Nothing here is computed or aggregated: every value is a field the
 * session or the student profile already carries.
 */
export function ProfilePage(): JSX.Element {
  const { user } = useAuth();
  const { activeMembership } = useActiveOrganization();
  const canManageStudents = useHasCapability('students:manage');
  const { data: profile, isLoading } = useMyStudentProfile(!canManageStudents);
  const { data: personalSpace } = usePersonalSpace();
  const isPersonalSpace = useIsPersonalSpace();

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-4 p-4">
      <div>
        <h1 className="text-xl font-semibold">Profil Saya</h1>
        <p className="text-sm text-muted-foreground">
          Identitas akun Anda di Mahfuz dan cara Anda menghafal.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Akun</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3 text-sm">
          <div className="flex items-start gap-3">
            <User className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            <div className="flex flex-col">
              <span className="text-xs text-muted-foreground">Nama</span>
              <span className="font-medium">{user?.fullName ?? '-'}</span>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Mail className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            <div className="flex min-w-0 flex-col">
              <span className="text-xs text-muted-foreground">Email</span>
              <span className="break-all font-medium">{user?.email ?? '-'}</span>
            </div>
          </div>

          {activeMembership !== null && (
            <div className="flex items-start gap-3">
              <ShieldCheck
                className="mt-0.5 size-4 shrink-0 text-muted-foreground"
                aria-hidden="true"
              />
              <div className="flex flex-col gap-1">
                <span className="text-xs text-muted-foreground">
                  {isPersonalSpace ? 'Ruang aktif' : 'Peran di organisasi aktif'}
                </span>
                <span className="flex flex-wrap items-center gap-2">
                  <Badge variant="secondary">{roleLabel(activeMembership.roleName)}</Badge>
                  {/* Only a personal space's name is readable by its owner:
                      a pondok's name comes from `organizations:read`, which
                      a santri does not hold. Naming it here would mean
                      inventing it. */}
                  {isPersonalSpace && personalSpace !== null && personalSpace !== undefined && (
                    <span className="text-muted-foreground">{personalSpace.name}</span>
                  )}
                </span>
              </div>
            </div>
          )}

          <p className="text-xs leading-relaxed text-muted-foreground">
            Nama dan email belum dapat diubah dari dalam aplikasi. Untuk mengganti kata sandi,
            gunakan tautan &ldquo;Lupa kata sandi&rdquo; di halaman masuk.
          </p>
        </CardContent>
      </Card>

      {!canManageStudents && isLoading && <Skeleton className="h-48 w-full" />}

      {profile !== null && profile !== undefined && <PreferredMethodCard student={profile} />}

      {!canManageStudents && !isLoading && (profile === null || profile === undefined) && (
        <Alert>
          <AlertDescription>
            Anda belum terdaftar sebagai santri di ruang ini, jadi belum ada pengaturan hafalan yang
            bisa disetel.
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Lainnya</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          {profile !== null && profile !== undefined && (
            <Button asChild variant="outline" size="sm">
              <Link to="/hafalan">
                <BookOpen className="size-4" aria-hidden="true" />
                Hafalan saya
              </Link>
            </Button>
          )}
          <Button asChild variant="outline" size="sm">
            <Link to="/account/sessions">Sesi &amp; Perangkat</Link>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
